import React, { useState, useEffect } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useNotifications } from '@toolpad/core/useNotifications'
import axios from '../../services/axiosInstance'
import EditIcon from '@mui/icons-material/Edit'
import CloseIcon from '@mui/icons-material/Close'

const OtpDialog = ({ open, handleClose, phone, new2024BatchMembers, pin, onVerify }) => {
  const notifications = useNotifications()

  const [otp, setOtp] = useState('')
  const [timer, setTimer] = useState(30)
  const [isResendDisabled, setIsResendDisabled] = useState(true)

  useEffect(() => {
    let intervalId

    if (open) {
      intervalId = startTimer()
    }

    return () => clearInterval(intervalId)
  }, [open])

  const startTimer = () => {
    setTimer(30)
    setIsResendDisabled(true)

    const intervalId = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(intervalId)
          setIsResendDisabled(false)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return intervalId
  }

  const handleResendOtp = async () => {
    try {
      if (phone.length !== 10) {
        notifications.show('Please enter a valid 10-digit mobile number', {
          severity: 'error',
          autoHideDuration: 5000
        })
        return
      }
      await axios.post('/otp/request', {
        ...(new2024BatchMembers ? { isPinNotAssigned: true } : { pin }),
        phoneNumber: `+91${phone}`
      })
      startTimer()
      notifications.show(`OTP has been resent to ${phone}`, {
        severity: 'info',
        autoHideDuration: 3000
      })
    } catch (error) {
      console.error('Error resending OTP:', error)
      notifications.show('Error occurred while resending OTP', {
        severity: 'error',
        autoHideDuration: 3000
      })
    }
  }

  const handleOtpVerification = async () => {
    try {
      if (otp.length !== 6) {
        notifications.show('Please enter a valid 6-digit OTP', {
          severity: 'error',
          autoHideDuration: 3000
        })
        return
      }
      await axios.post('/user/verify', {
        phoneNumber: `+91${phone}`,
        otp,
        ...(new2024BatchMembers ? { isPinNotAssigned: true } : { pin })
      })
      onVerify()
    } catch (error) {
      if (error.response) {
        // Server responded with a status other than 2xx
        console.error('API Error:', error.response.data)
        window.alert(error.response.data.message || 'Something went wrong')
      } else if (error.request) {
        // Request was made but no response received
        console.error('No response:', error.request)
        window.alert('No response from server. Please try again.')
      } else {
        // Other errors (like setting up the request)
        console.error('Error:', error.message)
        window.alert('Error: ' + error.message)
      }
      notifications.show('Error occurred while verifying OTP', {
        severity: 'error',
        autoHideDuration: 3000
      })
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='responsive-dialog-title'
      fullWidth
      disableEnforceFocus
      disablePortal
    >
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 2,
        pb: 0
      }}
      >
        <Typography variant='h6'>
          OTP has been sent to +91 {phone}
          <Button
            variant='text' onClick={handleClose}
            endIcon={<EditIcon sx={{ ml: -0.7, width: '16px' }} />}
            color='primary'
            sx={{ fontSize: '16px', fontWeight: 600, textTransform: 'none', ml: 1, mb: 0.5 }}
          >Edit
          </Button>
        </Typography>
        <CloseIcon sx={{ width: 28, cursor: 'pointer' }} onClick={handleClose} />
      </Box>
      <DialogContent>
        <DialogContentText>
          <TextField
            sx={{ my: 1 }}
            required
            id='outlined-required'
            label='OTP'
            placeholder='Enter 6 digit OTP'
            fullWidth
            length={6}
            value={otp}
            type='tel'
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              maxLength: 6
            }}
            onChange={(e) => setOtp(e.target.value)}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box direction='row' display='flex' alignItems='center'>
              <Typography variant='body2'>
                Didn't receive the OTP?
              </Typography>
              <Button
                variant='text'
                color='primary'
                onClick={handleResendOtp}
                disabled={isResendDisabled}
                sx={{ ml: 0.5, fontWeight: 600 }}
              >
                Resend
              </Button>
            </Box>
            {timer > 0 && <Typography>00:{timer < 10 ? `0${timer}` : timer}</Typography>}
          </Box>
        </DialogContentText>
      </DialogContent>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
        <Button autoFocus onClick={handleClose} variant='outlined' color='primary' sx={{ mr: 2 }}>
          Cancel
        </Button>
        <Button
          onClick={() => handleOtpVerification(otp)}
          autoFocus
          variant='contained' c
          olor='primary'
        >
          Verify
        </Button>
      </Box>
    </Dialog>
  )
}

export default OtpDialog
